import { useAuthUser } from "react-auth-kit";
import { getRegistryURL } from "./Registry";
import { buildDeleteOptions, buildGetOptions, buildPostOptions, fetchWithErrorHandling } from "../FetchUtils";

export enum ToolboxBuildArch {
  ARM64 = "ARM64",
  X86_64 = "X86_64",
}

export interface IToolboxBuild {
  id: string;
  name: string;
  creationDate: number;
  version: string;
  branch: string;
  arch: ToolboxBuildArch;
  commitHash?: string;
}

const endpoint = (action: string): string => {
  return `${getRegistryURL()}/v1/registry/toolbox-build/${action}`;
};

export const listToolboxBuilds = async (accessToken: string) => {
  return fetchWithErrorHandling(endpoint("all"), buildGetOptions(accessToken));
};

export const uploadToolboxBuild = async (
  accessToken: string,
  name: string,
  version: string,
  branch: string,
  arch: ToolboxBuildArch,
  file: File,
  commitHash?: string,
) => {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("version", version);
  formData.append("branch", branch);
  formData.append("arch", arch);
  if (commitHash) {
    formData.append("commitHash", commitHash);
  }
  formData.append("file", file);

  const options = buildPostOptions(accessToken, null, formData);
  return fetchWithErrorHandling(endpoint("upload"), options);
};

export const editToolboxBuild = async (accessToken: string, id: string, name: string, version: string, branch: string) => {
  const options = buildPostOptions(accessToken, { id, name, version, branch });
  return fetchWithErrorHandling(endpoint("edit"), options);
};

export const removeToolboxBuild = async (accessToken: string, id: string) => {
  return fetchWithErrorHandling(endpoint(`${id}`), buildDeleteOptions(accessToken));
};

export const getToolboxBuild = async (accessToken: string, id: string) => {
  return fetchWithErrorHandling(endpoint(`${id}`), buildGetOptions(accessToken));
};

export const useToolboxBuildService = () => {
  const authUser = useAuthUser();
  const accessToken = authUser()?.access_token;

  return {
    listToolboxBuilds: () => listToolboxBuilds(accessToken),
    uploadToolboxBuild: (
      name: string,
      version: string,
      branch: string,
      arch: ToolboxBuildArch,
      file: File,
      commitHash?: string,
    ) => uploadToolboxBuild(accessToken, name, version, branch, arch, file, commitHash),
    editToolboxBuild: (id: string, name: string, version: string, branch: string) =>
      editToolboxBuild(accessToken, id, name, version, branch),
    removeToolboxBuild: (id: string) => removeToolboxBuild(accessToken, id),
    getToolboxBuild: (id: string) => getToolboxBuild(accessToken, id),
  };
};
